import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const steps = [
  {
    icon: 'ri-search-line',
    color: '#7AC943',
    title: 'Discover',
    description: 'Answer a few simple questions about your home and get a fixed-price quote in minutes.',
    imageUrl: 'https://public.readdy.ai/ai/img_res/5ee36bcfa392fa443dbd124b20f61a2e.jpg',
  },
  {
    icon: 'ri-price-tag-3-line',
    color: '#4CAF50',
    title: 'Choose',
    description: 'Compare your fixed-price options. No pushy salespeople, no day-of price changes.',
    imageUrl: 'https://readdy.ai/api/search-image?query=happy%20couple%20on%20sofa%20reviewing%20solar%20energy%20options%20on%20tablet%20device%2C%20smiling%20homeowners%20looking%20at%20quotes%2C%20cozy%20modern%20living%20room%2C%20warm%20daylight%2C%20lifestyle%20photography&width=400&height=280&seq=how-choose-001&orientation=landscape',
  },
  {
    icon: 'ri-tools-line',
    color: '#145A32',
    title: 'Install',
    description: 'Our MCS-certified engineers arrive on time and complete your installation neatly.',
    imageUrl: 'https://readdy.ai/api/search-image?query=professional%20solar%20panel%20installation%20crew%20on%20residential%20rooftop%20UK%2C%20workers%20in%20safety%20gear%20fitting%20solar%20panels%2C%20clear%20blue%20sky%2C%20photorealistic%20action%20photography%2C%20daytime%20bright%20sunlight&width=400&height=280&seq=how-install-001&orientation=landscape',
  },
  {
    icon: 'ri-smartphone-line',
    color: '#3F4652',
    title: 'Save',
    description: 'Track your energy generation and savings in real-time through the smart monitoring app.',
    imageUrl: 'https://readdy.ai/api/search-image?query=homeowner%20checking%20solar%20energy%20monitoring%20app%20on%20smartphone%20showing%20energy%20generation%20and%20savings%20dashboard%2C%20modern%20home%20interior%2C%20happy%20person%2C%20bright%20clean%20setting&width=400&height=280&seq=how-save-001&orientation=landscape',
  },
];

export default function HowItWorks() {
  const headingAnim = useScrollAnimation();
  const gridAnim = useScrollAnimation();

  return (
    <section className="bg-white py-12 md:py-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Heading */}
        <div
          ref={headingAnim.ref}
          className={`transition-all duration-700 ${headingAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#1F2933] text-center mb-10">
            How does Renewables Made Easy work?
          </h2>
        </div>

        {/* 4-photo grid */}
        <div ref={gridAnim.ref}>
          {/* Mobile: swipe slider */}
          <div className="md:hidden">
            <div className="flex items-center justify-end mb-3 px-0.5">
              <span className="text-[11px] text-[#4CAF50] font-semibold flex items-center gap-1">
                <i className="ri-drag-move-line text-xs" /> Swipe steps
              </span>
            </div>
            <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory pb-4 [&::-webkit-scrollbar]:hidden -mx-4 px-4">
              {steps.map((step, i) => (
                <div
                  key={i}
                  className="snap-start flex-shrink-0 w-[75vw] flex flex-col rounded-xl overflow-hidden bg-[#f7f8fa] group"
                >
                  <div className="h-40 overflow-hidden relative">
                    <img
                      src={step.imageUrl}
                      alt={step.title}
                      className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-white flex items-center justify-center text-[#145A32] font-black text-xs">
                      {i + 1}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0"
                        style={{ background: step.color }}
                      >
                        <i className={`${step.icon} text-white text-sm`} />
                      </div>
                      <span className="font-heading font-bold text-[#1F2933] text-sm">{step.title}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-1.5 mt-1">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`rounded-full transition-all ${i === 0 ? 'w-4 h-1.5 bg-[#4CAF50]' : 'w-1.5 h-1.5 bg-gray-300'}`}
                />
              ))}
            </div>
          </div>

          {/* Desktop: 4-col grid */}
          <div className="hidden md:grid grid-cols-4 gap-4 md:gap-5">
            {steps.map((step, i) => (
              <div
                key={i}
                style={{ transitionDelay: `${i * 120}ms` }}
                className={`flex flex-col rounded-xl overflow-hidden bg-[#f7f8fa] hover:-translate-y-2 transition-all duration-300 group ${gridAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <div className="h-44 overflow-hidden relative">
                  <img
                    src={step.imageUrl}
                    alt={step.title}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-white flex items-center justify-center text-[#145A32] font-black text-xs">
                    {i + 1}
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                      style={{ background: step.color }}
                    >
                      <i className={`${step.icon} text-white text-sm`} />
                    </div>
                    <span className="font-heading font-bold text-[#1F2933] text-base">{step.title}</span>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Learn more */}
        <div className={`text-center mt-8 transition-all duration-700 delay-500 ${gridAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <button
            onClick={() => document.getElementById('quote-section')?.scrollIntoView({ behavior: 'smooth' })}
            className="shimmer-btn text-white font-bold px-8 py-3.5 rounded-full text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
          >
            Get a fixed price now
          </button>
        </div>
      </div>
    </section>
  );
}
